/* ==========================================================================
   SKNBODY HOMEPAGE BRAND HEADER
   File: assets/skn-home-header.js

   Responsibilities:
   - Header becomes sticky after reaching the top of the viewport
   - Smooth anchor navigation
   - Active section highlighting
   - Mobile / tablet menu
   - Focus management and keyboard accessibility
   - JOIN email focus
   - SHOP / BAG drawer integration with safe fallbacks
   - Cart count synchronization
   - Shopify Theme Editor support
   ========================================================================== */

(function () {
  'use strict';

  const HEADER_SELECTOR = '[data-skn-home-header]';
  const TABLET_BREAKPOINT = 1240;

  const initializedHeaders = new WeakSet();
  const cleanupMap = new WeakMap();

  /* ==========================================================================
     01. HELPERS
     ========================================================================== */

  function getConfig(header) {
    const globalConfig = window.SKN_HOME_HEADER_CONFIG || {};

    return {
      sectionId:
        header.dataset.sectionId ||
        globalConfig.sectionId ||
        '',

      headerSelector:
        globalConfig.headerSelector ||
        `#${header.id}`,

      emailInputSelector:
        globalConfig.emailInputSelector ||
        '#skn-join-email',

      anchorOffset:
        Number(globalConfig.anchorOffset) || 76
    };
  }

  function getFocusableElements(container) {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',')
      )
    ).filter(function (element) {
      return (
        !element.hasAttribute('hidden') &&
        element.getAttribute('aria-hidden') !== 'true' &&
        element.offsetParent !== null
      );
    });
  }

  function getJoinEmailInput(config) {
    const selectors = [
      config.emailInputSelector,
      '#skn-join-email',
      '#join-email-input',
      '#join-email',
      '[data-skn-join-email]',
      '#skn-join input[type="email"]'
    ];

    for (const selector of selectors) {
      if (!selector) continue;

      const input = document.querySelector(selector);

      if (input) return input;
    }

    return null;
  }

  function focusJoinEmail(config) {
    const input = getJoinEmailInput(config);

    if (!input) return;

    window.setTimeout(function () {
      try {
        input.focus({
          preventScroll: true
        });
      } catch (error) {
        input.focus();
      }

      input.classList.add('skn-join-input--focused');

      window.setTimeout(function () {
        input.classList.remove('skn-join-input--focused');
      }, 900);
    }, 520);
  }

  function getHeaderHeight(header) {
    return Math.round(header.getBoundingClientRect().height);
  }

  function getAnchorOffset(header, config) {
    const headerHeight = getHeaderHeight(header);

    return Math.max(
      headerHeight + 12,
      Number(config.anchorOffset) || headerHeight + 12
    );
  }

  function scrollToTarget(target, header, config, focusEmail) {
    if (!target) return false;

    const offset = getAnchorOffset(header, config);
    const targetTop =
      target.getBoundingClientRect().top +
      window.pageYOffset -
      offset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth'
    });

    if (focusEmail) {
      focusJoinEmail(config);
    }

    return true;
  }

  function dispatchDrawerEvent(name, detail) {
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable: true,
      detail: detail || {}
    });

    document.dispatchEvent(event);

    return event.defaultPrevented;
  }

  /* ==========================================================================
     02. INITIALIZE HEADER
     ========================================================================== */

  function initHeader(header) {
    if (!header || initializedHeaders.has(header)) return;

    initializedHeaders.add(header);

    const config = getConfig(header);

    const menuToggle = header.querySelector('[data-skn-menu-toggle]');
    const mobileMenu = header.querySelector('[data-skn-mobile-menu]');
    const menuBackdrop = header.querySelector('[data-skn-menu-backdrop]');

    const anchorLinks = Array.from(
      header.querySelectorAll('[data-skn-anchor]')
    );

    const shopLinks = Array.from(
      header.querySelectorAll('[data-skn-shop-open]')
    );

    const bagLinks = Array.from(
      header.querySelectorAll('[data-skn-bag-open]')
    );

    const scrollTopLinks = Array.from(
      header.querySelectorAll('[data-skn-scroll-top]')
    );

    const cartCountElements = Array.from(
      header.querySelectorAll('[data-skn-cart-count]')
    );

    const marketSelects = Array.from(
      header.querySelectorAll('[data-skn-market-select]')
    );

    let isMenuOpen = false;
    let stickyStart = 0;
    let rafId = null;
    let lastFocusedElement = null;
    let menuFocusTimer = null;
    let scrollLock = null;
    let destroyed = false;
    const portals = [];
    [menuBackdrop, mobileMenu].forEach(node => {
      if (!node) return;
      const marker = document.createComment('SKNBODY mobile menu home');
      node.parentNode.insertBefore(marker, node);
      portals.push({node, marker});
      document.body.appendChild(node);
      node.classList.add('is-viewport-menu');
    });
    if (mobileMenu) {
      mobileMenu.inert = true;
      mobileMenu.setAttribute('role', 'dialog');
      mobileMenu.setAttribute('aria-label', 'Navigation menu');
    }
    function lockPage() {
      if (scrollLock) return;
      const names = ['position','top','left','width','overflow'];
      scrollLock = {x: window.scrollX, y: window.scrollY, styles: names.map(name => [name, document.body.style.getPropertyValue(name), document.body.style.getPropertyPriority(name)])};
      document.body.style.setProperty('position', 'fixed');
      document.body.style.setProperty('top', -scrollLock.y + 'px');
      document.body.style.setProperty('left', '0');
      document.body.style.setProperty('width', '100%');
      document.body.style.setProperty('overflow', 'hidden');
    }
    function unlockPage() {
      if (!scrollLock) return;
      const saved = scrollLock; scrollLock = null;
      const html = document.documentElement;
      const behavior = html.style.getPropertyValue('scroll-behavior');
      const priority = html.style.getPropertyPriority('scroll-behavior');
      html.style.setProperty('scroll-behavior', 'auto', 'important');
      saved.styles.forEach(([name,value,priority]) => {
        if (value) document.body.style.setProperty(name,value,priority);
        else document.body.style.removeProperty(name);
      });
      window.scrollTo(saved.x, saved.y);
      if (behavior) html.style.setProperty('scroll-behavior', behavior, priority);
      else html.style.removeProperty('scroll-behavior');
    }

    /* ------------------------------------------------------------------------
       Create spacer for fixed header
       ------------------------------------------------------------------------ */

    const spacer = document.createElement('div');

    spacer.className = 'skn-home-header-spacer';
    spacer.setAttribute('aria-hidden', 'true');

    header.insertAdjacentElement('afterend', spacer);

    /* ------------------------------------------------------------------------
       Calculate sticky position
       ------------------------------------------------------------------------ */

    function calculateStickyStart() {
      if (destroyed) return;
      if (isMenuOpen) { updateMenuPosition(); return; }
      const wasSticky = header.classList.contains('is-sticky');

      if (wasSticky) {
        header.classList.remove('is-sticky');
        spacer.classList.remove('is-active');
      }

      stickyStart =
        header.getBoundingClientRect().top +
        window.pageYOffset;

      if (window.pageYOffset >= stickyStart) {
        header.classList.add('is-sticky');
        spacer.classList.add('is-active');
      }

      updateMenuPosition();
    }

    function updateStickyState() {
      if (destroyed) return;
      if (isMenuOpen) { updateMenuPosition(); return; }
      const shouldBeSticky =
        window.pageYOffset >= stickyStart;

      header.classList.toggle(
        'is-sticky',
        shouldBeSticky
      );

      spacer.classList.toggle(
        'is-active',
        shouldBeSticky
      );

      updateMenuPosition();
    }

    function onScroll() {
      if (rafId) return;

      rafId = window.requestAnimationFrame(function () {
        updateStickyState();
        updateActiveNavigation();
        rafId = null;
      });
    }

    /* ==========================================================================
       03. MOBILE MENU POSITION
       ========================================================================== */

    function updateMenuPosition() {
      if (destroyed || (!mobileMenu && !menuBackdrop)) return;
      const rect = header.getBoundingClientRect();
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const menuTop = Math.max(0, Math.round(rect.bottom));
      [mobileMenu, menuBackdrop].forEach(node => {
        if (!node) return;
        node.style.top = menuTop + 'px';
        node.style.height = Math.max(0, Math.floor(bottom - menuTop)) + 'px';
        node.style.bottom = 'auto';
      });
    }

    /* ==========================================================================
       04. MOBILE MENU OPEN / CLOSE
       ========================================================================== */

    function openMenu() {
      if (
        !menuToggle ||
        !mobileMenu ||
        isMenuOpen
      ) {
        return;
      }

      isMenuOpen = true;
      lastFocusedElement = document.activeElement;

      updateMenuPosition();

      menuToggle.setAttribute(
        'aria-expanded',
        'true'
      );

      menuToggle.setAttribute(
        'aria-label',
        'Close menu'
      );

      mobileMenu.setAttribute('aria-hidden', 'false');
      mobileMenu.setAttribute('aria-modal', 'true');
      mobileMenu.inert = false;

      mobileMenu.classList.add('is-open');
      header.classList.add('is-menu-open');

      if (menuBackdrop) {
        menuBackdrop.classList.add('is-open');
        menuBackdrop.setAttribute(
          'aria-hidden',
          'false'
        );
      }

      document.documentElement.classList.add(
        'skn-home-menu-active'
      );

      document.body.classList.add(
        'skn-home-menu-active'
      );

      lockPage();
      updateMenuPosition();
      const focusableElements = getFocusableElements(mobileMenu);
      if (focusableElements.length) {
        menuFocusTimer = window.setTimeout(function () {
          if (isMenuOpen && !destroyed) focusableElements[0].focus({preventScroll:true});
        }, 80);
      }
    }

    function closeMenu(options) {
      const settings = Object.assign(
        {
          restoreFocus: true
        },
        options || {}
      );

      if (
        !menuToggle ||
        !mobileMenu ||
        !isMenuOpen
      ) {
        return;
      }

      isMenuOpen = false;
      window.clearTimeout(menuFocusTimer);
      mobileMenu.inert = true;
      mobileMenu.removeAttribute('aria-modal');

      menuToggle.setAttribute(
        'aria-expanded',
        'false'
      );

      menuToggle.setAttribute(
        'aria-label',
        'Open menu'
      );

      mobileMenu.setAttribute(
        'aria-hidden',
        'true'
      );

      mobileMenu.classList.remove('is-open');
      header.classList.remove('is-menu-open');

      if (menuBackdrop) {
        menuBackdrop.classList.remove('is-open');
        menuBackdrop.setAttribute(
          'aria-hidden',
          'true'
        );
      }

      document.documentElement.classList.remove(
        'skn-home-menu-active'
      );

      document.body.classList.remove(
        'skn-home-menu-active'
      );

      unlockPage();
      updateStickyState();

      if (
        settings.restoreFocus &&
        lastFocusedElement &&
        typeof lastFocusedElement.focus === 'function'
      ) {
        lastFocusedElement.focus({preventScroll:true});
      }

      lastFocusedElement = null;
    }

    function handleMenuNavigation(event) {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      closeMenu({restoreFocus:false});
    }
    function handleMenuBackdrop() { closeMenu(); }
    function handlePageRestore() {
      closeMenu({restoreFocus:false});
      unlockPage();
      calculateStickyStart(); updateStickyState(); refreshCartCount();
    }
    function toggleMenu() {
      if (isMenuOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    }

    function trapMenuFocus(event) {
      if (
        !isMenuOpen ||
        event.key !== 'Tab' ||
        !mobileMenu
      ) {
        return;
      }

      const focusableElements = [menuToggle].concat(getFocusableElements(mobileMenu)).filter(Boolean);

      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    /* ==========================================================================
       05. ANCHOR NAVIGATION
       ========================================================================== */

    function handleAnchorClick(event) {
      const link = event.currentTarget;
      const targetId = link.dataset.sknAnchor;

      if (!targetId) return;

      const target =
        document.getElementById(targetId);

      if (!target) {
        return;
      }

      event.preventDefault();

      const shouldFocusEmail =
        link.hasAttribute('data-skn-focus-email');

      closeMenu({
        restoreFocus: false
      });

      scrollToTarget(
        target,
        header,
        config,
        shouldFocusEmail
      );

      if (history.pushState) {
        history.pushState(
          null,
          '',
          `#${targetId}`
        );
      }
    }

    function handleScrollTopClick(event) {
      const currentPath =
        window.location.pathname.replace(/\/+$/, '');

      const rootPath =
        new URL(
          event.currentTarget.href,
          window.location.origin
        ).pathname.replace(/\/+$/, '');

      if (
        currentPath !== rootPath &&
        currentPath !== ''
      ) {
        return;
      }

      event.preventDefault();

      closeMenu({
        restoreFocus: false
      });

      window.scrollTo({
        top: 0,
        behavior: window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
          ? 'auto'
          : 'smooth'
      });
    }

    /* ==========================================================================
       06. ACTIVE NAVIGATION STATE
       ========================================================================== */

    function getAvailableSections() {
      const sectionMap = new Map();

      anchorLinks.forEach(function (link) {
        const targetId = link.dataset.sknAnchor;

        if (
          targetId &&
          !sectionMap.has(targetId)
        ) {
          const section =
            document.getElementById(targetId);

          if (section) {
            sectionMap.set(targetId, section);
          }
        }
      });

      return sectionMap;
    }

    function updateActiveNavigation() {
      const sections = getAvailableSections();

      if (!sections.size) return;

      const headerHeight = getHeaderHeight(header);
      const activationLine =
        window.pageYOffset +
        headerHeight +
        Math.min(
          window.innerHeight * 0.28,
          240
        );

      let activeId = null;

      sections.forEach(function (section, id) {
        const sectionTop =
          section.getBoundingClientRect().top +
          window.pageYOffset;

        if (sectionTop <= activationLine) {
          activeId = id;
        }
      });

      anchorLinks.forEach(function (link) {
        const isActive =
          link.dataset.sknAnchor === activeId;

        link.classList.toggle(
          'is-active',
          isActive
        );

        if (isActive) {
          link.setAttribute(
            'aria-current',
            'location'
          );
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    /* ==========================================================================
       07. SHOP DRAWER
       ========================================================================== */

    function handleShopClick(event) {
      closeMenu({
        restoreFocus: false
      });

      if (
        window.SKNShopDrawer &&
        typeof window.SKNShopDrawer.open ===
          'function'
      ) {
        event.preventDefault();

        window.SKNShopDrawer.open({
          view: 'products',
          trigger: event.currentTarget
        });

        return;
      }

      const drawerHandled = dispatchDrawerEvent(
        'skn:shop-open',
        {
          view: 'products',
          trigger: event.currentTarget
        }
      );

      if (drawerHandled) {
        event.preventDefault();
      }
    }

    /* ==========================================================================
       08. BAG / CART DRAWER
       ========================================================================== */

    function openShopifyCartDrawer(trigger) {
      const cartDrawer =
        document.querySelector('cart-drawer');

      if (
        !cartDrawer ||
        typeof cartDrawer.open !== 'function'
      ) {
        return false;
      }

      try {
        cartDrawer.open(trigger);
        return true;
      } catch (error) {
        return false;
      }
    }

    function handleBagClick(event) {
      closeMenu({
        restoreFocus: false
      });

      if (
        window.SKNShopDrawer &&
        typeof window.SKNShopDrawer.open ===
          'function'
      ) {
        event.preventDefault();

        window.SKNShopDrawer.open({
          view: 'cart',
          trigger: event.currentTarget
        });

        return;
      }

      if (
        openShopifyCartDrawer(event.currentTarget)
      ) {
        event.preventDefault();
        return;
      }

      const drawerHandled = dispatchDrawerEvent(
        'skn:bag-open',
        {
          view: 'cart',
          trigger: event.currentTarget
        }
      );

      if (drawerHandled) {
        event.preventDefault();
      }
    }

    /* ==========================================================================
       09. CART COUNT
       ========================================================================== */

    function updateCartCount(count) {
      const normalizedCount = Math.max(
        0,
        Number(count) || 0
      );

      cartCountElements.forEach(function (
        element
      ) {
        const previousCount = Number(
          element.textContent.trim()
        );

        element.textContent =
          String(normalizedCount);

        element.classList.toggle(
          'is-empty',
          normalizedCount === 0
        );

        const bagLink =
          element.closest('[data-skn-bag-open]');

        if (bagLink) {
          bagLink.setAttribute(
            'aria-label',
            `Open shopping bag, ${normalizedCount} ${
              normalizedCount === 1
                ? 'item'
                : 'items'
            }`
          );
        }

        if (
          previousCount !== normalizedCount &&
          normalizedCount > 0
        ) {
          element.classList.remove(
            'is-updated'
          );

          void element.offsetWidth;

          element.classList.add(
            'is-updated'
          );

          window.setTimeout(function () {
            element.classList.remove(
              'is-updated'
            );
          }, 260);
        }
      });
    }

    function refreshCartCount() {
      if (!window.fetch) return;

      const root =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';

      fetch(`${root}cart.js`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error(
              'Unable to read cart'
            );
          }

          return response.json();
        })
        .then(function (cart) {
          updateCartCount(cart.item_count);
        })
        .catch(function () {
          /* Keep Liquid-rendered count as fallback. */
        });
    }

    function handleCartUpdated(event) {
      const detail = event.detail || {};

      if (
        typeof detail.item_count === 'number'
      ) {
        updateCartCount(detail.item_count);
        return;
      }

      if (
        detail.cart &&
        typeof detail.cart.item_count === 'number'
      ) {
        updateCartCount(
          detail.cart.item_count
        );

        return;
      }

      refreshCartCount();
    }

    /* ==========================================================================
       10. MARKET / CURRENCY SELECTOR
       ========================================================================== */

    function handleMarketChange(event) {
      const select = event.currentTarget;
      const form = select.form || select.closest('form');

      if (!form) return;

      select.setAttribute('aria-busy', 'true');

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }

    /* ==========================================================================
       11. KEYBOARD / RESIZE
       ========================================================================== */

    function handleDocumentKeydown(event) {
      if (
        event.key === 'Escape' &&
        isMenuOpen
      ) {
        event.preventDefault();
        closeMenu();
        return;
      }

      trapMenuFocus(event);
    }

    function handleResize() {
      calculateStickyStart();

      if (
        window.innerWidth >
          TABLET_BREAKPOINT &&
        isMenuOpen
      ) {
        closeMenu({
          restoreFocus: false
        });
      }

      updateMenuPosition();
    }

    /* ==========================================================================
       11. EVENT LISTENERS
       ========================================================================== */

    if (menuToggle) {
      menuToggle.addEventListener(
        'click',
        toggleMenu
      );
    }

    if (menuBackdrop) {
      menuBackdrop.addEventListener(
        'click',
        handleMenuBackdrop
      );
    }

    if (mobileMenu) mobileMenu.addEventListener('click', handleMenuNavigation);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateMenuPosition);
      window.visualViewport.addEventListener('scroll', updateMenuPosition);
    }

    anchorLinks.forEach(function (link) {
      link.addEventListener(
        'click',
        handleAnchorClick
      );
    });

    scrollTopLinks.forEach(function (link) {
      link.addEventListener(
        'click',
        handleScrollTopClick
      );
    });

    shopLinks.forEach(function (link) {
      link.addEventListener(
        'click',
        handleShopClick
      );
    });

    bagLinks.forEach(function (link) {
      link.addEventListener(
        'click',
        handleBagClick
      );
    });

    marketSelects.forEach(function (select) {
      select.addEventListener(
        'change',
        handleMarketChange
      );
    });

    document.addEventListener(
      'keydown',
      handleDocumentKeydown
    );

    window.addEventListener(
      'scroll',
      onScroll,
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true
      }
    );

    window.addEventListener(
      'pageshow',
      handlePageRestore
    );

    document.addEventListener(
      'skn:cart-updated',
      handleCartUpdated
    );

    document.addEventListener(
      'cart:updated',
      handleCartUpdated
    );

    document.addEventListener(
      'cart:refresh',
      refreshCartCount
    );

    /* ==========================================================================
       12. INITIAL STATE
       ========================================================================== */

    calculateStickyStart();
    updateStickyState();
    updateActiveNavigation();
    refreshCartCount();

    window.setTimeout(function () {
      calculateStickyStart();
      updateStickyState();
    }, 250);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        calculateStickyStart();
        updateStickyState();
      });
    }

    /* ==========================================================================
       13. CLEANUP
       ========================================================================== */

    function cleanup() {
      window.clearTimeout(menuFocusTimer);
      if (mobileMenu) mobileMenu.removeEventListener('click', handleMenuNavigation);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateMenuPosition);
        window.visualViewport.removeEventListener('scroll', updateMenuPosition);
      }
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      if (menuToggle) {
        menuToggle.removeEventListener(
          'click',
          toggleMenu
        );
      }

      if (menuBackdrop) {
        menuBackdrop.removeEventListener(
          'click',
          handleMenuBackdrop
        );
      }

      anchorLinks.forEach(function (link) {
        link.removeEventListener(
          'click',
          handleAnchorClick
        );
      });

      scrollTopLinks.forEach(function (link) {
        link.removeEventListener(
          'click',
          handleScrollTopClick
        );
      });

      shopLinks.forEach(function (link) {
        link.removeEventListener(
          'click',
          handleShopClick
        );
      });

      bagLinks.forEach(function (link) {
        link.removeEventListener(
          'click',
          handleBagClick
        );
      });

      marketSelects.forEach(function (select) {
        select.removeEventListener(
          'change',
          handleMarketChange
        );
      });

      document.removeEventListener(
        'keydown',
        handleDocumentKeydown
      );

      window.removeEventListener(
        'scroll',
        onScroll
      );

      window.removeEventListener(
        'resize',
        handleResize
      );

      window.removeEventListener(
        'pageshow',
        handlePageRestore
      );

      document.removeEventListener(
        'skn:cart-updated',
        handleCartUpdated
      );

      document.removeEventListener(
        'cart:updated',
        handleCartUpdated
      );

      document.removeEventListener(
        'cart:refresh',
        refreshCartCount
      );

      closeMenu({
        restoreFocus: false
      });

      unlockPage();
      destroyed = true;
      portals.forEach(({node, marker}) => {
        node.classList.remove('is-viewport-menu');
        if (marker.parentNode) marker.replaceWith(node);
        else node.remove();
      });
      spacer.remove();

      initializedHeaders.delete(header);
      cleanupMap.delete(header);
    }

    cleanupMap.set(header, cleanup);
  }

  /* ==========================================================================
     14. INITIALIZE ALL HEADERS
     ========================================================================== */

  function initAllHeaders(root) {
    const context = root || document;

    if (
      context.matches &&
      context.matches(HEADER_SELECTOR)
    ) {
      initHeader(context);
    }

    context
      .querySelectorAll(HEADER_SELECTOR)
      .forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initAllHeaders(document);
      }
    );
  } else {
    initAllHeaders(document);
  }

  /* ==========================================================================
     15. SHOPIFY THEME EDITOR SUPPORT
     ========================================================================== */

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initAllHeaders(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    function (event) {
      const headers = [];

      if (
        event.target.matches &&
        event.target.matches(HEADER_SELECTOR)
      ) {
        headers.push(event.target);
      }

      event.target
        .querySelectorAll(HEADER_SELECTOR)
        .forEach(function (header) {
          headers.push(header);
        });

      headers.forEach(function (header) {
        const cleanup = cleanupMap.get(header);

        if (cleanup) cleanup();
      });
    }
  );
})();
